/* Sticky quote button (sections/sticky-quote-button.liquid).

   Waits until the section named in data-show-after-section (the hero, which
   carries its own Enquire button) has scrolled away, plus any extra
   data-show-after pixels. Hides again while the section named in
   data-hide-at (the quote form) is on screen: a floating "Get a Quote" laid
   over the form invites you to do the thing you are already doing, and on a
   phone it would sit on the fields.

   Pressing it scrolls smoothly to the form, and so does every other link on
   the page that points at the form (the hero's Enquire button, the card
   buttons). The theme's own [data-anchor] helper offsets by the header bar
   alone; here the form's scroll-margin carries the whole sticky stack. */
if (!customElements.get('sticky-quote-button')) {
  class StickyQuoteButton extends HTMLElement {
    connectedCallback() {
      this.stack = this.querySelector('[data-fab-stack]');
      this.links = Array.from(this.querySelectorAll('[data-fab-link]'));
      this.link = this.querySelector('[data-quote-link]');
      if (!this.stack || !this.links.length) return;
      this.showAfter = Number(this.dataset.showAfter || 0);
      this.atTarget = false;
      this.pastStart = true;
      this.preview = this.hasAttribute('data-design-mode');

      const start = this.dataset.showAfterSection ? document.getElementById(this.dataset.showAfterSection) : null;
      if (start) {
        this.pastStart = false;
        /* The section counts as gone once it is behind the sticky header,
           not only once it leaves the window. */
        const headerHeight = this.headerHeight();
        this.startObserver = new IntersectionObserver(
          ([entry]) => {
            /* Past it only once it has left through the top, not before it. */
            this.pastStart = !entry.isIntersecting && entry.boundingClientRect.top < headerHeight;
            this.update();
          },
          { rootMargin: `-${headerHeight}px 0px 0px 0px` }
        );
        this.startObserver.observe(start);
      }

      const target = this.dataset.hideAt ? document.getElementById(this.dataset.hideAt) : null;
      if (target) {
        this.observer = new IntersectionObserver(
          ([entry]) => {
            this.atTarget = entry.isIntersecting;
            this.update();
          },
          { threshold: 0.12 }
        );
        this.observer.observe(target);
      }

      this.frame = 0;
      this.onScroll = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.update(); });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });

      /* Theme editor: keep it on screen while its section is selected. */
      this.onSelect = () => { this.preview = true; this.update(); };
      this.onDeselect = () => { this.preview = this.hasAttribute('data-design-mode'); this.update(); };
      document.addEventListener('shopify:section:select', this.onSelect);
      document.addEventListener('shopify:section:deselect', this.onDeselect);

      this.onClick = (event) => this.scrollToTarget(event);
      document.addEventListener('click', this.onClick);

      this.update();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.startObserver) this.startObserver.disconnect();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.frame) cancelAnimationFrame(this.frame);
      document.removeEventListener('shopify:section:select', this.onSelect);
      document.removeEventListener('shopify:section:deselect', this.onDeselect);
      document.removeEventListener('click', this.onClick);
    }

    scrollToTarget(event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a[href*="#"]');
      if (!link) return;
      /* A popup trigger opens the quote popup instead, when one is on the page. */
      if (link.hasAttribute('data-quote-popup') && document.querySelector('[data-quote-popup-dialog]')) return;
      const url = new URL(link.href, window.location.href);
      if (url.pathname !== window.location.pathname || url.hash.length < 2) return;
      const id = decodeURIComponent(url.hash.slice(1));
      /* Its own Get a Quote link, and any link to the form it watches. */
      if ((!this.link || link !== this.link) && id !== this.dataset.hideAt) return;
      const target = document.getElementById(id);
      if (!target) return;

      event.preventDefault();
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      /* scrollIntoView honours the target's scroll-margin-top, which already
         clears the sticky header stack. */
      target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      /* Lazy images above the form can still load while the scroll is under
         way and push it down; once the scroll stops, check and correct once. */
      const settle = () => {
        const offset = target.getBoundingClientRect().top - (parseFloat(getComputedStyle(target).scrollMarginTop) || 0);
        if (Math.abs(offset) > 2) target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
      };
      if ('onscrollend' in window) window.addEventListener('scrollend', settle, { once: true });
      else setTimeout(settle, 1200);
      if (window.location.hash !== url.hash) history.pushState(null, '', url.hash);
      /* Keyboard and screen-reader users land on the form, not back at the top. */
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    }

    /* The theme keeps its sticky header stack in --cstm-tab-header-height. */
    headerHeight() {
      const probe = document.createElement('div');
      probe.style.cssText = 'position:absolute;visibility:hidden;height:var(--cstm-tab-header-height, 0px)';
      document.body.appendChild(probe);
      const height = probe.offsetHeight;
      probe.remove();
      return height;
    }

    update() {
      const scrolled = window.scrollY >= this.showAfter;
      const show = this.preview || (this.pastStart && scrolled && !this.atTarget);
      this.stack.classList.toggle('is-visible', show);
      this.links.forEach((link) => {
        link.setAttribute('aria-hidden', show ? 'false' : 'true');
        link.tabIndex = show ? 0 : -1;
      });
    }
  }

  customElements.define('sticky-quote-button', StickyQuoteButton);
}
